import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Table,
  Spinner,
  Alert,
  FormGroup,
  Label,
  Form,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  fetchRegions,
  createRegion,
  updateRegion,
  deleteRegion,
} from "../../services/apiServices";
import { formatDate } from "../../helpers/utils";

const Regions = () => {
  const [regions, setRegions] = useState([]);
  const [modal, setModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [currentRegion, setCurrentRegion] = useState({
    _id: null,
    name: "",
  });
  const [deleteModal, setDeleteModal] = useState(false);
  const [regionToDelete, setRegionToDelete] = useState(null);

  // Fetch regions on component mount
  useEffect(() => {
    getRegions();
  }, []);

  const getRegions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchRegions();
      if (response.status) {
        setRegions(response.data.regions);
      } else {
        setError(response.message || "Failed to load regions");
      }
    } catch (err) {
      setError(err.message || "Failed to load regions");
    } finally {
      setLoading(false);
    }
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      // Reset form when opening
      setEditMode(false);
      setCurrentRegion({ _id: null, name: "" });
    }
  };

  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
    if (!deleteModal) {
      setRegionToDelete(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentRegion({ ...currentRegion, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentRegion.name.trim()) {
      setError("Region name is required");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (editMode) {
        const response = await updateRegion({
          id: currentRegion._id,
          name: currentRegion.name,
        });
        if (response.status) {
          setSuccess("Region updated successfully");
          // Update the regions list
          setRegions(
            regions.map((region) =>
              region._id === currentRegion._id ? response.data.region : region
            )
          );
          toggleModal();
        } else {
          setError(response.message || "Failed to update region");
        }
      } else {
        const response = await createRegion({ name: currentRegion.name });
        if (response.status) {
          setSuccess("Region created successfully");
          setRegions([...regions, response.data.region.data]);
          toggleModal();
        } else {
          setError(response.message || "Failed to create region");
        }
      }
    } catch (err) {
      setError(
        err.message || `Failed to ${editMode ? "update" : "create"} region`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (region) => {
    setCurrentRegion({ ...region });
    setEditMode(true);
    setModal(true);
  };

  const handleDelete = async () => {
    if (!regionToDelete) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await deleteRegion(regionToDelete._id);
      if (response.status) {
        setSuccess("Region deleted successfully");
        // Remove deleted region from the list
        setRegions(
          regions.filter((region) => region._id !== regionToDelete._id)
        );
        toggleDeleteModal();
      } else {
        setError(response.message || "Failed to delete region");
      }
    } catch (err) {
      setError(err.message || "Failed to delete region");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (region) => {
    setRegionToDelete(region);
    setDeleteModal(true);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Master" breadcrumbItem="Regions" />

          {success && (
            <Alert color="success" className="mb-4">
              {success}
            </Alert>
          )}

          {error && (
            <Alert color="danger" className="mb-4">
              {error}
            </Alert>
          )}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="card-title">Regions List</h4>
                    <Button color="primary" onClick={toggleModal}>
                      <i className="mdi mdi-plus-circle me-1"></i> Add Region
                    </Button>
                  </div>

                  {loading && !modal && !deleteModal ? (
                    <div className="text-center my-5">
                      <Spinner color="primary" />
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table table-hover table-centered table-nowrap mb-0">
                        <thead>
                          <tr>
                            <th scope="col">Name</th>
                            <th scope="col">Created At</th>
                            <th scope="col">Updated At</th>
                            <th scope="col" style={{ width: "120px" }}>
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {regions.length > 0 ? (
                            regions.map((region) => (
                              <tr key={region._id}>
                                <td>{region.name}</td>
                                <td>{formatDate(region.createdAt)}</td>
                                <td>{formatDate(region.updatedAt)}</td>
                                <td>
                                  <Button
                                    color="primary"
                                    size="sm"
                                    className="me-2"
                                    onClick={() => handleEdit(region)}
                                  >
                                    <i className="mdi mdi-pencil"></i>
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => confirmDelete(region)}
                                  >
                                    <i className="mdi mdi-trash-can"></i>
                                  </Button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center">
                                No regions found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Add/Edit Region Modal */}
          <Modal isOpen={modal} toggle={toggleModal}>
            <ModalHeader toggle={toggleModal}>
              {editMode ? "Edit Region" : "Add New Region"}
            </ModalHeader>
            <ModalBody>
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label for="name">Region Name</Label>
                  <Input
                    type="text"
                    name="name"
                    id="name"
                    placeholder="Enter region name"
                    value={currentRegion.name}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
                <div className="d-flex justify-content-end mt-4">
                  <Button
                    color="light"
                    type="button"
                    className="me-2"
                    onClick={toggleModal}
                  >
                    Cancel
                  </Button>
                  <Button color="primary" type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {editMode ? "Updating..." : "Creating..."}
                      </>
                    ) : (
                      <>{editMode ? "Update" : "Create"}</>
                    )}
                  </Button>
                </div>
              </Form>
            </ModalBody>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
            <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
            <ModalBody>
              <p>
                Are you sure you want to delete the region{" "}
                <strong>{regionToDelete?.name}</strong>? This action cannot be
                undone.
              </p>
              <div className="d-flex justify-content-end mt-4">
                <Button
                  color="light"
                  type="button"
                  className="me-2"
                  onClick={toggleDeleteModal}
                >
                  Cancel
                </Button>
                <Button
                  color="danger"
                  onClick={handleDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Deleting...
                    </>
                  ) : (
                    "Delete"
                  )}
                </Button>
              </div>
            </ModalBody>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Regions;
